import {
  Avatar,
  Button,
  ButtonGroup,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router';

import {pushRoute} from '../../routes';
import ConfirmVotingDialog from './components/ConfirmVotingDialog';
import DetailCompetitorDialog from './components/DetailCompetitorDialog';
import InvalidVotingDialog from './components/InvalidVotingDialog';
import LabelCountElection from './components/LabelCountElection';
import Screen from './screen';
import {
  loadPlateDetailAsync,
  loadPlatesAsync,
  requestVotingAsync,
} from './store/actions';
import {
  getCompetitors,
  getElectionInfo,
  getPLateData,
  getPlates,
  getRound,
} from './store/selectors';
import {ICompetitor, IInputVoting, IPlate} from './store/types';

const Election: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {electionId} = useParams();
  const [checkers, setCheckers] = useState<ICompetitor[]>([]);
  const [
    currentCompetitor,
    setCurrentCompetitor,
  ] = useState<ICompetitor | null>(null);
  const [confirmedVote, setConfirmedVote] = useState(false);
  const [openInvalidDialog, setOpenInvalidDialog] = useState(false);
  const [openDetailCompetitorDialog, setOpenDetailCompetitorDialog] = useState(
    false
  );
  const competitors: ICompetitor[] = useSelector(getCompetitors);
  const {loading} = useSelector(getElectionInfo);
  const plate = useSelector(getPLateData);
  const plates: IPlate[] = useSelector(getPlates);
  const round = useSelector(getRound);

  const maxOptions: number =
    plate && plate.configs.choice_max ? plate.configs.choice_max : 0;

  const minOptions: number =
    plate && plate.configs.choice_min ? plate.configs.choice_min : 0;

  useEffect(() => {
    if (electionId) {
      dispatch(loadPlateDetailAsync.request(electionId));
    }
  }, [dispatch, electionId]);

  // Quando pagina carregada e ainda nao tiver pleitos, deve atualizar lista de pleitos no estado
  useEffect(() => {
    if (!loading || plates.length > 0) {
      return;
    }

    dispatch(loadPlatesAsync.request());
  }, [plates, loading, dispatch]);

  const getMaxOptions = () => {
    if (checkMinMaxZero()) {
      return competitors.length;
    } else {
      return maxOptions;
    }
  };

  const handleToggle = (value: ICompetitor) => {
    const currentIndex = checkItem(value);

    const newChecked = [...checkers];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckers(newChecked);
  };

  const checkItem = (item: ICompetitor) => {
    return checkers.findIndex((p: ICompetitor) => p.id === item.id);
  };

  const confirmVoting = () => {
    if (!electionId) {
      return;
    }

    const data: IInputVoting = {
      plate_uuid: electionId.toString(),
      competitors: checkers.map(check => check.id),
    };

    dispatch(requestVotingAsync.request(data));
    dispatch(pushRoute('resultVotes'));
  };

  const goToHome = () => {
    dispatch(pushRoute('home'));
  };

  const showDetailCompetitor = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    competitor: ICompetitor
  ) => {
    event.stopPropagation();
    setCurrentCompetitor(competitor);
    setOpenDetailCompetitorDialog(true);
  };

  const clearChecked = () => {
    setCheckers([]);
  };

  const whiteVoting = () => {
    clearChecked();
    openConfirmDialog();
  };

  const openConfirmDialog = () => {
    if (
      checkMinMaxZero() ||
      (checkers.length >= minOptions && checkers.length <= maxOptions)
    ) {
      setConfirmedVote(true);
    } else {
      setOpenInvalidDialog(true);
    }
  };

  const checkMinMaxZero = () => {
    return minOptions === 0 && maxOptions === 0;
  };

  const closeConfirmDialog = (action: string) => {
    setConfirmedVote(false);

    switch (action) {
      case 'cancel':
        clearChecked();
        break;

      case 'confirm':
        confirmVoting();
        break;
    }
  };

  const getLabelChoices = size => {
    if (size === 1) {
      return t('eleicao.voce-realizou-x-escolhas_one', {
        numeroDeEscolhas: checkers.length,
      });
    } else {
      return t('eleicao.voce-realizou-x-escolhas', {
        numeroDeEscolhas: checkers.length,
      });
    }
  };

  if (!plate || !round || loading) {
    return null;
  }

  return (
    <Screen className="election">
      <div className="full-width secondary-toolbar">
        <h3 className="mt-0 ">
          <strong>{plate.name}</strong>
        </h3>
        <h4 className="secondary-text mb-0">
          {t('eleicao.turno')} {round.laps}
        </h4>
        {round.description && (
          <small className="secondary-text-white"> {round.description}</small>
        )}
      </div>

      <div className="px-15-xs full-width">
        <h2 className="primary-title text-center">{t('eleicao.votar')}</h2>

        <LabelCountElection
          min={minOptions}
          max={maxOptions}
          count={checkers.length}
          fixed={true}
        />

        {competitors && (
          <List component="nav" className="list-white" aria-label="eleições">
            {competitors.map(value => {
              const labelId = `checkbox-list-label-${value.id}`;

              return (
                <ListItem
                  key={value.id}
                  role={undefined}
                  dense={true}
                  button={true}
                  onClick={() => handleToggle(value)}
                  className={checkItem(value) !== -1 ? 'selected' : ''}
                >
                  <Checkbox
                    edge="start"
                    checked={checkItem(value) !== -1}
                    tabIndex={-1}
                    disableRipple={true}
                    inputProps={{'aria-labelledby': labelId}}
                  />
                  <ListItemText id={labelId} primary={value.name} />
                  <div onClick={event => showDetailCompetitor(event, value)}>
                    <ListItemAvatar>
                      <Avatar
                        sizes="large"
                        alt={value.name}
                        src={value.thumb}
                      />
                    </ListItemAvatar>
                  </div>
                </ListItem>
              );
            })}
          </List>
        )}
        <Grid container={true} alignItems="center" justify="space-between">
          <p className="default-subtitle text-center">
            <b>{getLabelChoices(checkers.length)}</b>
          </p>
        </Grid>
        <br />
        <br />
        {minOptions === 0 && (
          <Button
            className={`button-default btn-color-white full-width`}
            type="button"
            variant="contained"
            color="primary"
            onClick={whiteVoting}
          >
            {t('eleicao.votar-em-branco')}
          </Button>
        )}
        <ButtonGroup className="full-width mt-8 mb-36" color="primary">
          <Button
            className={`button-default btn-color-grey full-width`}
            type="button"
            variant="contained"
            color="primary"
            onClick={goToHome}
          >
            {t('geral.cancelar')}
          </Button>
          <Button
            className={`button-default btn-color-orange full-width`}
            type="button"
            color="primary"
            variant="contained"
            onClick={clearChecked}
          >
            {t('geral.corrigir')}
          </Button>
          <Button
            className={`button-default btn-green full-width`}
            type="button"
            color="primary"
            variant="contained"
            onClick={openConfirmDialog}
          >
            {t('geral.confirmar')}
          </Button>
        </ButtonGroup>
      </div>

      <ConfirmVotingDialog
        party={checkers}
        min={minOptions}
        max={getMaxOptions()}
        open={confirmedVote}
        onClose={closeConfirmDialog}
      />

      <InvalidVotingDialog
        open={openInvalidDialog}
        min={minOptions}
        max={maxOptions}
        onClose={() => setOpenInvalidDialog(false)}
      />

      {currentCompetitor && (
        <DetailCompetitorDialog
          open={openDetailCompetitorDialog}
          competitor={currentCompetitor}
          onClose={() => setOpenDetailCompetitorDialog(false)}
        />
      )}
    </Screen>
  );
};

export default Election;



// WEBPACK FOOTER //
// ./src/features/election/Election.tsx